import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { reduceInterestTableNewsData, reducePropertiesTableNewsData } from '../../../Reducer/ReduceTableNewsData';
import { getInterestNews, getPropertiesNews } from '../../../Service/NewsService';
import { tokenExpired } from '../../../Store/Auth/action';
import { filterFinishedLoading, filterSubmitted } from '../../../Store/Filter/action';

import useFilterIsActual from '../UseFilterIsActual';
import useGetLabels from '../UseGetLabels';

import { NEWS_FILTER } from '../../../Constants/Interest/Filter';
import { PageableTableDTO } from '../../../Interface/Hook/Interest/PageableTableDTO';
import { INITIAL_TABLE_STATE, PageableTableState } from '../../../Interface/Hook/Interest/PageableTableState';
import { ErrorResponse } from '../../../Interface/ErrorResponse';
import { FetchInterface, HookInterface, ReducerInterface } from '../../../Interface/News/NewsInterface';

const useGetNews = (
    labels: string[],
    dateFrom: string,
    dateTo: string,
    page: number,
    fetcher: FetchInterface,
    reducer: ReducerInterface,
): PageableTableDTO => {
    const dispatch = useDispatch();
    const filterIsActual = useFilterIsActual(NEWS_FILTER);
    const { allLabels } = useGetLabels(true);
    const [{ error, data, headers, loading, pageLoading, total }, setState] = useState<PageableTableState>(
        INITIAL_TABLE_STATE,
    );

    const fetchData = useCallback(() => {
        if (!filterIsActual) dispatch(filterSubmitted());
        if (filterIsActual) {
            setState(current => ({ ...current, pageLoading: true }));
            fetcher(labels, dateFrom, dateTo, page).then(
                result => {
                    const { data: newData, headers: newHeaders } = reducer(result.labels, allLabels);

                    setState({
                        error: false,
                        loading: false,
                        pageLoading: false,
                        total: result.total,
                        data: page > 1 ? [...data, ...newData] : newData,
                        headers: newHeaders,
                    });
                    dispatch(filterFinishedLoading());
                },
                (errorResult: ErrorResponse) => {
                    errorResult.code === 403
                        ? dispatch(tokenExpired())
                        : setState({
                            error: true,
                            data,
                            headers,
                            total,
                            loading: false,
                            pageLoading: false,
                        });
                    dispatch(filterFinishedLoading());
                },
            );
        }
    }, [dispatch, labels, dateFrom, dateTo, page, filterIsActual, data, headers, allLabels, total]);

    useEffect(() => fetchData(), [labels, dateFrom, dateTo, page]);

    return { data, error, headers, loading, pageLoading, total };
};

export const useGetInterestNews: HookInterface = (labels, dateFrom, dateTo, page) =>
    useGetNews(labels, dateFrom, dateTo, page, getInterestNews, reduceInterestTableNewsData);

export const useGetPropertiesNews: HookInterface = (labels, dateFrom, dateTo, page) =>
    useGetNews(labels, dateFrom, dateTo, page, getPropertiesNews,reducePropertiesTableNewsData);
